import { getCustomTranslationValue } from '@/data-viz/data-source.interface';
import { Page, RoutingService } from '@/services/routing/routing.service';
import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { ButtonStyle } from '@/ui/button/ui-button';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { frenchSort } from '@/utils/toolkit/array/sort.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, firstValueFrom, type Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, startWith, switchMap } from 'rxjs/operators';
import { DashboardAccessesManagementModalComponent } from '../../components/dashboard-accesses-management-modal/dashboard-accesses-management-modal';
import type { AdvizeoDialogModalButton } from '../../components/modals/advizeo-dialog-modal/advizeo-dialog-modal';
import { isBusinessIndicatorRight } from '../../interfaces/BusinessIndicator.interface';
import {
    type DashboardId,
    type DashboardInteractiveElements,
    type DashboardLayout,
    DashboardLayoutType,
    DashboardMode,
    DashboardScopeType,
    type TailoredDashboard,
    type TailoredDashboardId,
    type TailoredDashboardLayout
} from '../../interfaces/Dashboard.interface';
import type { Profile, ProfileId } from '../../interfaces/Profile.interface';
import { FeatureType } from '../../interfaces/Role.interface';
import { AdvizeoModalService } from '../../services/advizeo-modal.service';
import type { AppState } from '../../store/app.state';
import { DashboardsStoreActions, DashboardsStoreSelectors } from '../../store/entities/dashboards';
import { ErrorHandlingProvider } from '../error-handling/error-handling.provider';
import { InformationProvider } from '../information/information.provider';
import { ProfileProvider } from '../profile/profile.provider';
import { ProfilesProvider } from '../profiles/profiles.provider';
import { ProjectsProvider } from '../projects/projects.provider';
import { LegacyDashboardRight, USER_DASHBOARD } from '../rights.constants';
import { RolesProvider } from '../roles/roles.provider';
import type { DashboardModeDescription } from './../../interfaces/Dashboard.interface';
import { CAN_USE_INTERNAL_PROJECT_DASHBOARDS } from './../rights.constants';
import { AdvizeoCoreTailoredDashboardsProvider } from './advizeo-core-tailored-dashboards.provider';
import type { DashboardsProvider } from './dashboards.provider';
import { getLegacyDashboardIds, getTailoredDashboardIds, isLegacyDashboard } from './dashboards.utils';
import { LegacyDashboardsProvider } from './legacy-dashboards.provider';

interface DashboardSelectionInterface {
    type: DashboardScopeType;
    warning: string | null;
    info: string;
    count?: number;
    canCreateNew: boolean;
}

@Injectable()
export class DashboardsProviderAdapter implements DashboardsProvider {
    private _advizeoCoreTailoredDashboardsProvider = inject(AdvizeoCoreTailoredDashboardsProvider);
    private _informationProvider = inject(InformationProvider);
    private _translateService = inject(TranslateService);
    private _advizeoModalService = inject(AdvizeoModalService);
    private _errorHandlingProvider = inject(ErrorHandlingProvider);
    private _rolesProvider = inject(RolesProvider);
    private _profileProvider = inject(ProfileProvider);
    private _profilesProvider = inject(ProfilesProvider);
    private _referenceListsService = inject(ReferenceListsService);
    private _store = inject<Store<AppState>>(Store);
    private _legacyDashboardsProvider = inject(LegacyDashboardsProvider);
    private _projectsProvider = inject(ProjectsProvider);
    private _routingService = inject(RoutingService);

    private _dashboardCreated$ = new Subject<DashboardId>();

    private displayWarningMessageWhenAllowedDashboardCountIsReached() {
        this._advizeoModalService.openDialog({
            title: this._translateService.instant('DASHBOARD.NEW_DASHBOARD'),
            message: this._translateService.instant('DASHBOARD.WARNING_MESSAGE'),
            buttons: [
                {
                    text: this._translateService.instant('BUTTONS.CANCEL'),
                    onClick: 'cancel',
                    color: SharedUiColorNameV2.PRIMARY,
                    buttonStyle: ButtonStyle.OUTLINE
                }
            ]
        });
    }

    private requestDashboardCreation(type: DashboardScopeType, preciseDashboardType: Boolean) {
        const modal = this._advizeoModalService.openOneFieldModal({
            title: preciseDashboardType
                ? this._translateService.instant('DASHBOARD.NEW_DASHBOARD') +
                  ' "' +
                  this._translateService.instant('DASHBOARD.SELECT_TYPE_MODAL.' + type) +
                  '"'
                : 'DASHBOARD.NEW_DASHBOARD',
            fieldLabel: this._translateService.instant('DASHBOARD.NAME'),
            submitValidators: []
        });
        modal.afterClosed().then(output => {
            if (output) {
                firstValueFrom(
                    this.createNewDashboard$(output.fieldValue, type).pipe(
                        catchError(error =>
                            this._errorHandlingProvider.handleAPIError$(error, { defaultError: 'DASHBOARD.CREATION.ERROR' })
                        )
                    )
                ).then(dashboardId => {
                    this._informationProvider.showToast(this._translateService.instant('DASHBOARD.CREATION.SUCCESS'), 'success');
                    this.fetchDashboards().then(() => {
                        this._dashboardCreated$.next(dashboardId);
                    });
                    firstValueFrom(this._routingService.getCurrentPage$()).then(currentPage => {
                        if (currentPage !== Page.DASHBOARDS) {
                            this._routingService.goTo(Page.DASHBOARDS, {});
                        }
                    });
                });
            }
        });
    }

    selectDashboardCreationModal() {
        return firstValueFrom(
            combineLatest([
                this.canCreateUserDashboard$(),
                this.canCreateNewUserDashboard$(),
                this.canUseInternalProjectDashboard$(),
                this.canCreateNewProjectDashboard$()
            ]).pipe(
                map(
                    ([
                        canCreateUserDashboard,
                        canCreatedNewUserDashboard,
                        canUseInternalProjectDashboards,
                        canCreatedNewProjectDashboard
                    ]) =>
                        this.selectDashboardCreationModalFromRights(
                            canUseInternalProjectDashboards,
                            canCreatedNewProjectDashboard,
                            canCreateUserDashboard,
                            canCreatedNewUserDashboard
                        )
                )
            )
        );
    }

    selectDashboardCreationModalFromRights(
        canUseInternalProjectDashboards: boolean,
        canCreatedNewProjectDashboard: boolean,
        canCreateUserDashboard: boolean,
        canCreatedNewUserDashboard: boolean
    ) {
        const availableDashboardTypeToCreation: DashboardSelectionInterface[] = [
            {
                type: DashboardScopeType.PROJECT,
                warning: 'DASHBOARD.SELECT_TYPE_MODAL.PROJECT_DASHBOARD.WARNING',
                info: 'DASHBOARD.SELECT_TYPE_MODAL.PROJECT_DASHBOARD.INFO',
                canCreateNew: canCreatedNewProjectDashboard
            }
        ];
        if (canUseInternalProjectDashboards) {
            availableDashboardTypeToCreation.push({
                type: DashboardScopeType.INTERNAL_PROJECT,
                warning: null,
                info: 'DASHBOARD.SELECT_TYPE_MODAL.INTERNAL_DASHBOARD.INFO',
                canCreateNew: true
            });
        } else if (canCreateUserDashboard) {
            availableDashboardTypeToCreation.push({
                type: DashboardScopeType.USER,
                warning: 'DASHBOARD.SELECT_TYPE_MODAL.USER_DASHBOARD.WARNING',
                info: 'DASHBOARD.SELECT_TYPE_MODAL.USER_DASHBOARD.INFO',
                canCreateNew: canCreatedNewUserDashboard
            });
        }
        this.selectDashboardType(availableDashboardTypeToCreation);
    }

    private selectDashboardType(availableDashboardTypeToCreation: DashboardSelectionInterface[]) {
        if (availableDashboardTypeToCreation.length === 0) {
            this.displayWarningMessageWhenAllowedDashboardCountIsReached();
        } else if (availableDashboardTypeToCreation.length === 1) {
            availableDashboardTypeToCreation[0].canCreateNew
                ? this.requestDashboardCreation(availableDashboardTypeToCreation[0].type, false)
                : this.displayWarningMessageWhenAllowedDashboardCountIsReached();
        } else {
            const infos: string[] = [];
            const warnings: string[] = [];

            const buttons: AdvizeoDialogModalButton[] = [
                {
                    text: this._translateService.instant('BUTTONS.CANCEL'),
                    onClick: 'cancel',
                    color: SharedUiColorNameV2.PRIMARY,
                    buttonStyle: ButtonStyle.OUTLINE
                }
            ];
            availableDashboardTypeToCreation.forEach(dashboardInterface => {
                if (dashboardInterface.canCreateNew) {
                    infos.push(dashboardInterface.info);
                    buttons.push({
                        text: this._translateService.instant('DASHBOARD.SELECT_TYPE_MODAL.' + dashboardInterface.type),
                        onClick: () => this.requestDashboardCreation(dashboardInterface.type, true),
                        color: SharedUiColorNameV2.PRIMARY
                    });
                } else {
                    if (isNotNullish(dashboardInterface.warning)) warnings.push(dashboardInterface.warning);
                }
            });

            this._advizeoModalService.openDialog({
                title: this._translateService.instant('DASHBOARD.SELECT_TYPE_MODAL.TITLE'),
                message: this.generateSelectionDashboardTypeMessage(infos, warnings),
                buttons
            });
        }
    }

    private generateSelectionDashboardTypeMessage(infos: string[], warnings: string[]): string {
        let message = '';
        if (infos.length > 0) {
            message += this._translateService.instant('DASHBOARD.SELECT_TYPE_MODAL.MESSAGE_INFOS') + '\n\n';
            infos.forEach(info => (message += this._translateService.instant(info) + '\n\n'));
            message += '\n';
        }
        if (warnings.length > 0) {
            message += this._translateService.instant('DASHBOARD.SELECT_TYPE_MODAL.MESSAGE_WARNINGS') + '\n\n';
            warnings.forEach(warning => (message += this._translateService.instant(warning) + '\n\n'));
        }
        return message;
    }

    getUserDashboardCreationModal() {
        this.requestDashboardCreation(DashboardScopeType.USER, true);
    }

    async requestDashboardEdition(dashboardId: DashboardId) {
        if (!isLegacyDashboard(dashboardId)) {
            const dashboardName = await firstValueFrom(this.getDashboardName$(dashboardId));
            const modal = this._advizeoModalService.openOneFieldModal({
                title: this._translateService.instant('DASHBOARD.EDIT.TITLE'),
                fieldLabel: this._translateService.instant('DASHBOARD.NAME'),
                fieldValue: dashboardName,
                submitValidators: [
                    {
                        validate: name => isNotNullish(name),
                        errorMessage: this._translateService.instant('ERRORS.FORM.REQUIRED')
                    },
                    {
                        validate: name => name !== dashboardName,
                        errorMessage: this._translateService.instant('ERRORS.FORM.INVALID')
                    }
                ]
            });
            modal.afterClosed().then(output => {
                if (output) {
                    firstValueFrom(
                        this.editDashboard$(dashboardId, output.fieldValue).pipe(
                            catchError(error =>
                                this._errorHandlingProvider.handleAPIError$(error, { defaultError: 'DASHBOARD.EDIT.ERROR' })
                            )
                        )
                    ).then(() => {
                        this._informationProvider.showToast(this._translateService.instant('DASHBOARD.EDIT.SUCCESS'), 'success');
                        this._store.dispatch(
                            new DashboardsStoreActions.UpdateDashboard({ dashboardId, dashboard: { name: output.fieldValue } })
                        );
                    });
                }
            });
        }
    }

    requestDashboardDeletion(dashboardId: DashboardId) {
        if (!isLegacyDashboard(dashboardId)) {
            const modal = this._advizeoModalService.openConfirmActionModal({
                title: this._translateService.instant('DASHBOARD.DELETE.CONFIRMATION'),
                subtitle: this._translateService.instant('DASHBOARD.DELETE.WARNING'),
                textToType: this._translateService.instant('BUTTONS.DELETE'),
                buttons: [
                    {
                        text: this._translateService.instant('BUTTONS.VALIDATE'),
                        code: 'validate',
                        color: SharedUiColorNameV2.PRIMARY
                    }
                ]
            });

            modal.afterClosed().then(validated => {
                if (validated) {
                    firstValueFrom(
                        this.deleteDashboard(dashboardId).pipe(
                            catchError(error =>
                                this._errorHandlingProvider.handleAPIError$(error, { defaultError: 'DASHBOARD.DELETE.ERROR' })
                            )
                        )
                    ).then(() => {
                        this._informationProvider.showToast(this._translateService.instant('DASHBOARD.DELETE.SUCCESS'), 'success');
                        this._store.dispatch(new DashboardsStoreActions.DeleteDashboard({ dashboardId }));
                    });
                }
            });
        }
    }

    requestDashboardAccessesManagement(dashboardId: DashboardId) {
        this._advizeoModalService.openAdvizeoModal(DashboardAccessesManagementModalComponent, { dashboardId });
    }

    async requestInternalDashboardConversion(dashboardId: DashboardId) {
        const canConvertInternalProjectDashboardToProjectDashboard = await firstValueFrom(
            this.canConvertInternalProjectDashboardToProjectDashboard$(dashboardId)
        );

        if (canConvertInternalProjectDashboardToProjectDashboard) {
            this._advizeoModalService.openDialog({
                title: this._translateService.instant('DASHBOARD.INTERNAL_PROJECT_CONVERSION.TITLE'),
                message: this._translateService.instant('DASHBOARD.INTERNAL_PROJECT_CONVERSION.WARNING'),
                buttons: [
                    {
                        text: 'BUTTONS.VALIDATE',
                        onClick: () => this.convertInternalProjectDashboardToProjectDashboard(dashboardId),
                        color: SharedUiColorNameV2.PRIMARY
                    },
                    {
                        text: 'BUTTONS.CANCEL',
                        onClick: 'cancel',
                        color: SharedUiColorNameV2.PRIMARY,
                        buttonStyle: ButtonStyle.OUTLINE
                    }
                ]
            });
        } else {
            this._advizeoModalService.openDialog({
                title: this._translateService.instant('DASHBOARD.INTERNAL_PROJECT_CONVERSION_ERROR.TITLE'),
                message: this._translateService.instant('DASHBOARD.INTERNAL_PROJECT_CONVERSION_ERROR.WARNING'),
                buttons: [
                    {
                        text: 'BUTTONS.OK',
                        onClick: 'cancel',
                        color: SharedUiColorNameV2.PRIMARY
                    }
                ]
            });
        }
    }

    private createNewDashboard$(dashboardName: string, type: DashboardScopeType): Observable<DashboardId> {
        return this._advizeoCoreTailoredDashboardsProvider.createDashboard$(dashboardName, type);
    }

    private editDashboard$(dashboardId: TailoredDashboardId, dashboardName: string): Observable<void> {
        return this._advizeoCoreTailoredDashboardsProvider.editDashboard$(dashboardId, dashboardName);
    }

    private deleteDashboard(dashboardId: TailoredDashboardId): Observable<void> {
        return this._advizeoCoreTailoredDashboardsProvider.deleteDashboard$(dashboardId);
    }

    private _getDashboardIds$(): Observable<DashboardId[]> {
        return combineLatest([
            this._legacyDashboardsProvider
                .getLegacyDashboards$()
                .pipe(map(dashboards => dashboards.map(dashboard => dashboard.dashboardRight))),
            this.getStableTailoredDashboards$().pipe(map(dashboards => dashboards.map(dashboard => dashboard.dashboardId)))
        ]).pipe(map(([legacyDashboardIds, tailoredDashboardIds]) => [...legacyDashboardIds, ...tailoredDashboardIds]));
    }

    fetchDashboards(): Promise<TailoredDashboard[]> {
        this._store.dispatch(new DashboardsStoreActions.FetchDashboards());
        return firstValueFrom(this._advizeoCoreTailoredDashboardsProvider.fetchDashboards$())
            .then(dashboards => {
                this._store.dispatch(new DashboardsStoreActions.FetchDashboardsSucceeded({ dashboards }));
                return dashboards;
            })
            .catch(err => {
                this._store.dispatch(new DashboardsStoreActions.FetchDashboardsFailed());
                throw err;
            });
    }

    getDashboardIds$(): Observable<DashboardId[]> {
        return this._getDashboardIds$().pipe(
            switchMap(dashboardIds => {
                return combineLatest(
                    dashboardIds.map(dashboardId => {
                        return this.getDashboardName$(dashboardId).pipe(
                            map(name => ({
                                dashboardId,
                                name
                            }))
                        );
                    })
                );
            }),
            map(dashboard => {
                return dashboard.sort((a, b) => a.name.localeCompare(b.name)).map(dashboard => dashboard.dashboardId);
            })
        );
    }

    getDashboardIdsByTypes$(types: DashboardScopeType[]): Observable<DashboardId[]> {
        return combineLatest([
            this._legacyDashboardsProvider.getLegacyDashboards$().pipe(
                map(dashboards =>
                    dashboards.map(dashboard => {
                        return {
                            dashboardId: dashboard.dashboardRight,
                            dashboardType: DashboardScopeType.STANDARD
                        };
                    })
                )
            ),
            this.getStableTailoredDashboards$().pipe(
                map(dashboards =>
                    dashboards.map(dashboard => {
                        return { dashboardId: dashboard.dashboardId, dashboardType: dashboard.type };
                    })
                )
            )
        ]).pipe(
            map(([legacyDashboard, tailoredDashboard]) =>
                [...legacyDashboard, ...tailoredDashboard]
                    .filter(dashboard => types.includes(dashboard.dashboardType))
                    .map(dashboard => dashboard.dashboardId)
            ),
            switchMap(dashboardIds => {
                return combineLatest(
                    dashboardIds.map(dashboardId => {
                        return this.getDashboardName$(dashboardId).pipe(
                            map(name => ({
                                dashboardId,
                                name
                            }))
                        );
                    })
                );
            }),
            map(dashboard => {
                return dashboard.sort((a, b) => a.name.localeCompare(b.name)).map(dashboard => dashboard.dashboardId);
            })
        );
    }

    getDashboardName$(dashboardId: DashboardId): Observable<string> {
        if (isLegacyDashboard(dashboardId)) {
            return combineLatest([
                this._profileProvider.getUserLanguage$(),
                this._legacyDashboardsProvider.getLegacyDashboard$(dashboardId)
            ]).pipe(
                switchMap(([language, legacyDashboard]) => {
                    const roleName$: Observable<string> = this._rolesProvider
                        .getRoleByRoleCode$(this._rolesProvider.getDashboardRoleFromDasboardRight(dashboardId))
                        .pipe(
                            filter(isNotNullish),
                            switchMap(role => this._rolesProvider.getRoleName$(role))
                        );

                    if (legacyDashboard) {
                        return of(getCustomTranslationValue(legacyDashboard.dashboardName, language));
                    } else {
                        return roleName$;
                    }
                })
            );
        } else {
            return this.getTailoredDashboardById$(dashboardId).pipe(
                filter(isNotNullish),
                switchMap(dashboard =>
                    dashboard.name
                        ? of(dashboard.name)
                        : this._referenceListsService.getReferenceListItemLabel$(ReferenceListName.DASHBOARD_NAME, dashboard.code)
                )
            );
        }
    }

    getDashboardScopeType$(dashboardId: DashboardId): Observable<DashboardScopeType> {
        if (!isLegacyDashboard(dashboardId)) {
            return this.getTailoredDashboardById$(dashboardId).pipe(
                filter(isNotNullish),
                map(dashboard => dashboard.type)
            );
        } else {
            return of(DashboardScopeType.STANDARD);
        }
    }

    getDashboardNames$(dashboardId: DashboardId[]): Observable<string[]> {
        return combineLatestDeepObject(
            dashboardId.map(dashboardId => {
                return this.getDashboardName$(dashboardId);
            })
        ).pipe(map(dashboardNames => dashboardNames.sort(frenchSort)));
    }

    getDashboardCount$(dashboardIds: DashboardId[]): Observable<number> {
        const dashboardRoles: string[] = this._rolesProvider.getDashboardRolesFromDashboardRights(getLegacyDashboardIds(dashboardIds));
        const legacyDashboardCount$ = this._rolesProvider.getRolesByRoleCodes$(dashboardRoles).pipe(
            filter(isNotNullish),
            map(roles => {
                const legacyDashboardRoleCount = roles.filter(role => role.featureType === FeatureType.LEGACY_DASHBOARD).length;
                const rolesContainsBusinessIndicators = roles.some(role => role.featureType === FeatureType.BUSINESS_INDICATOR);
                return rolesContainsBusinessIndicators ? legacyDashboardRoleCount + 1 : legacyDashboardRoleCount;
            })
        );

        const tailoredDashboardCount$: Observable<number> = of(getTailoredDashboardIds(dashboardIds).length);
        return combineLatest([legacyDashboardCount$, tailoredDashboardCount$]).pipe(
            map(([legacyDashboardCount, tailoredDashboardCount]) => legacyDashboardCount + tailoredDashboardCount)
        );
    }

    getDashboardModes$(dashboardId: DashboardId): Observable<DashboardModeDescription[]> {
        if (!!dashboardId) {
            return combineLatestDeepObject([
                {
                    dashboardMode: DashboardMode.MULTI,
                    name: this._translateService.stream('DASHBOARD.MODES.MULTI')
                },
                {
                    dashboardMode: DashboardMode.MONO,
                    name: this._translateService.stream('DASHBOARD.MODES.MONO')
                }
            ]);
        }
        return of([]);
    }

    getDashboardModeName(dashboardMode: DashboardMode): string {
        switch (dashboardMode) {
            case DashboardMode.MONO:
                return this._translateService.instant('DASHBOARD.MODES.MONO');
            case DashboardMode.MULTI:
                return this._translateService.instant('DASHBOARD.MODES.MULTI');
        }
    }

    getDashboardLayout$(dashboardId: DashboardId, mode: DashboardMode): Observable<DashboardLayout> {
        if (isLegacyDashboard(dashboardId)) {
            return this._legacyDashboardsProvider.getLegacyDashboard$(dashboardId).pipe(
                filter(isNotNullish),
                map(legacyDashboard => {
                    return {
                        layoutType: DashboardLayoutType.LEGACY as DashboardLayoutType.LEGACY,
                        widgets:
                            mode === DashboardMode.MONO
                                ? this._legacyDashboardsProvider.getLegacyAdaptedDashboardWidget(legacyDashboard.listWidgetsMono)
                                : this._legacyDashboardsProvider.getLegacyAdaptedDashboardWidget(legacyDashboard.listWidgetsMulti)
                    };
                })
            );
        } else {
            return this._advizeoCoreTailoredDashboardsProvider.fetchDashboardLayout$(dashboardId, mode);
        }
    }

    canCreateProjectDashboard$(): Observable<boolean> {
        return this._profileProvider.isProjectAdmin$();
    }

    canCreateNewUserDashboard$(): Observable<boolean> {
        return this.getTailoredDashboards$().pipe(
            map(dashboards => {
                const numberOfUserDashboard = dashboards.filter(dashboard => dashboard.type === DashboardScopeType.USER).length;
                // TODO ISP-897
                if (1 > numberOfUserDashboard) {
                    return true;
                }

                return false;
            })
        );
    }

    canUseInternalProjectDashboard$(): Observable<boolean> {
        return this._profileProvider.hasRight$(CAN_USE_INTERNAL_PROJECT_DASHBOARDS);
    }

    canCreateUserDashboard$(): Observable<boolean> {
        return this._profileProvider.hasRight$(USER_DASHBOARD);
    }

    isDashboardEditable$(dashboardId: DashboardId): Observable<boolean> {
        if (isLegacyDashboard(dashboardId)) {
            return of(false);
        } else {
            return this.getTailoredDashboardById$(dashboardId).pipe(
                filter(isNotNullish),
                map(dashboard => dashboard.canBeUpdated)
            );
        }
    }

    hasAdminAccessOnDashboard$(dashboardId: DashboardId): Observable<boolean> {
        if (isLegacyDashboard(dashboardId)) {
            return this._profileProvider.isProjectAdmin$();
        } else {
            return this.getTailoredDashboardById$(dashboardId).pipe(
                filter(isNotNullish),
                map(dashboard => dashboard.canBeUpdated && dashboard.type !== DashboardScopeType.INTERNAL_PROJECT)
            );
        }
    }

    canManageAccessOnDashboard$(dashboardId: DashboardId): Observable<boolean> {
        if (dashboardId === LegacyDashboardRight.DSHB_BUSINESS_INDICATOR) return of(false);

        if (isLegacyDashboard(dashboardId)) {
            return this._profileProvider.isProjectAdmin$();
        } else {
            return this.getTailoredDashboardById$(dashboardId).pipe(
                filter(isNotNullish),
                map(
                    dashboard =>
                        dashboard.canBeUpdated &&
                        dashboard.type !== DashboardScopeType.INTERNAL_PROJECT &&
                        dashboard.type !== DashboardScopeType.USER
                )
            );
        }
    }

    saveDashboardModeLayouts(
        dashboardId: DashboardId,
        modeLayouts: { mode: DashboardMode; layout: TailoredDashboardLayout }[]
    ): Promise<void> {
        return firstValueFrom(
            this._advizeoCoreTailoredDashboardsProvider
                .saveDashboardModeLayouts$(dashboardId, modeLayouts)
                .pipe(
                    catchError(error =>
                        this._errorHandlingProvider.handleAPIError$(error, { defaultError: 'DASHBOARD.SAVE.ERROR', reThrow: true })
                    )
                )
        ).then(() => {
            this._informationProvider.showToast(this._translateService.instant('DASHBOARD.SAVE.SUCCESS'), 'success');
        });
    }

    getDashboardInteractiveElements(dashboardId: DashboardId, _mode: DashboardMode): Observable<DashboardInteractiveElements[]> {
        if (isLegacyDashboard(dashboardId)) {
            return this._legacyDashboardsProvider.getLegacyDashboard$(dashboardId).pipe(
                filter(isNotNullish),
                map(legacyDashboard => {
                    const dashboardInteractiveElements: DashboardInteractiveElements[] = [];
                    if (legacyDashboard.showDisableEstimatedToggle) dashboardInteractiveElements.push('estimatedToggle');
                    if (legacyDashboard.showPeriodsList) dashboardInteractiveElements.push('periodSelector');
                    return dashboardInteractiveElements;
                })
            );
        } else {
            return of(['periodSelector' as DashboardInteractiveElements]);
        }
    }

    private _dashboardProfilesAccessesUpdated$ = new Subject<DashboardId>();

    getDashboardProfileAccesses$(dashboardId: DashboardId): Observable<{ profile: Profile; hasAccess: boolean }[]> {
        let currentDashboardProfileIds$!: Observable<ProfileId[]>;
        if (isLegacyDashboard(dashboardId)) {
            currentDashboardProfileIds$ = this._dashboardProfilesAccessesUpdated$.pipe(
                filter(updateRightCode => updateRightCode === dashboardId),
                startWith(null),
                switchMap(() =>
                    isBusinessIndicatorRight(dashboardId)
                        ? of(dashboardId)
                        : this._rolesProvider.getRoleCodeFromDashboardRight$(dashboardId)
                ),
                switchMap(roleCode => this._rolesProvider.getRolesProfilesAccesses$(roleCode))
            );
        } else {
            currentDashboardProfileIds$ = this._dashboardProfilesAccessesUpdated$.pipe(
                filter(updatedDashboardId => updatedDashboardId === dashboardId),
                startWith(null),
                switchMap(() => {
                    return this._advizeoCoreTailoredDashboardsProvider.fetchDashboardProfilesAccesses$(dashboardId);
                })
            );
        }
        return combineLatest([this._profilesProvider.getProfilesWithoutAdvizeoProfiles$(), currentDashboardProfileIds$]).pipe(
            map(([profiles, dashboardProfileIds]) => {
                return profiles.map(profile => ({ profile, hasAccess: dashboardProfileIds.includes(profile.profileId) }));
            })
        );
    }

    updateDashboardProfileAccess(dashboardId: DashboardId, profileId: ProfileId, hasAccess: boolean) {
        let updateProfile$!: Observable<void>;
        if (isLegacyDashboard(dashboardId)) {
            if (isBusinessIndicatorRight(dashboardId)) {
                updateProfile$ = this._profilesProvider.updateRoleProfileAccess$(dashboardId as string, profileId, hasAccess);
            } else {
                updateProfile$ = this._rolesProvider
                    .getRoleCodeFromDashboardRight$(dashboardId)
                    .pipe(switchMap(roleCode => this._profilesProvider.updateRoleProfileAccess$(roleCode, profileId, hasAccess)));
            }
        } else {
            updateProfile$ = this._profilesProvider.updateDashboardProfileAccess$(dashboardId, profileId, hasAccess);
        }

        firstValueFrom(
            updateProfile$.pipe(
                catchError(error =>
                    this._errorHandlingProvider.handleAPIError$(error, {
                        reThrow: true,
                        defaultError: this._translateService.instant('DASHBOARD.ACCESSES_MANAGEMENT.UPDATE_ERROR')
                    })
                )
            )
        )
            .then(() => {
                this._dashboardProfilesAccessesUpdated$.next(dashboardId);
            })
            .catch(() => {
                this._dashboardProfilesAccessesUpdated$.next(dashboardId);
            });
    }

    canCreateNewProjectDashboard$(): Observable<boolean> {
        return combineLatest([this.getTailoredDashboards$(), this._projectsProvider.getUserCurrentProject$()]).pipe(
            map(([dashboards, currentProject]) => {
                const allowedDashboardCount =
                    isNotNullish(currentProject) && isNotNullish(currentProject.allowedDashboardCount)
                        ? currentProject.allowedDashboardCount
                        : null;
                const numberOfProjectDashboard = dashboards.filter(dashboard => dashboard.type === DashboardScopeType.PROJECT).length;

                if (allowedDashboardCount && allowedDashboardCount > numberOfProjectDashboard) {
                    return true;
                }

                return false;
            })
        );
    }

    areDashboardsLoading$(): Observable<boolean> {
        return this._store.pipe(select(DashboardsStoreSelectors.selectIsPending));
    }

    canConvertInternalProjectDashboardToProjectDashboard$(dashboardId: DashboardId): Observable<boolean> {
        if (!isLegacyDashboard(dashboardId)) {
            return combineLatest([
                this.isInternalProjectDashboard$(dashboardId),
                this.canUseInternalProjectDashboard$(),
                this.canCreateNewProjectDashboard$()
            ]).pipe(
                map(
                    ([isInternalProjectDashboard, canCreateInternalProject, canCreateNewProjectDashboard]) =>
                        isInternalProjectDashboard && canCreateInternalProject && canCreateNewProjectDashboard
                )
            );
        }
        return of(false);
    }

    private convertInternalProjectDashboardToProjectDashboard(dashboardId: DashboardId): Promise<void> {
        if (isLegacyDashboard(dashboardId)) {
            return Promise.resolve();
        }
        return firstValueFrom(
            this.canConvertInternalProjectDashboardToProjectDashboard$(dashboardId).pipe(
                filter(canConvertInternalProjectDashboardToProjectDashboard => canConvertInternalProjectDashboardToProjectDashboard),
                switchMap(() => {
                    return this._advizeoCoreTailoredDashboardsProvider.editDashboardType$(dashboardId, DashboardScopeType.PROJECT).pipe(
                        map(() => {
                            this._informationProvider.showToast(
                                this._translateService.instant('DASHBOARD.INTERNAL_PROJECT_CONVERSION.SUCCESS'),
                                'success'
                            );
                            this._store.dispatch(
                                new DashboardsStoreActions.UpdateDashboard({
                                    dashboardId,
                                    dashboard: { type: DashboardScopeType.PROJECT }
                                })
                            );
                        }),
                        catchError(error =>
                            this._errorHandlingProvider.handleAPIError$(error, {
                                defaultError: 'DASHBOARD.INTERNAL_PROJECT_CONVERSION.ERROR',
                                reThrow: true
                            })
                        )
                    );
                })
            )
        );
    }

    isInternalProjectDashboard$(dashboardId: DashboardId): Observable<boolean> {
        if (!isLegacyDashboard(dashboardId)) {
            return this.getTailoredDashboardById$(dashboardId).pipe(
                filter(isNotNullish),
                map(dashboard => dashboard && dashboard.type === DashboardScopeType.INTERNAL_PROJECT)
            );
        } else {
            return of(false);
        }
    }

    listenToDashboardCreated$(): Observable<DashboardId> {
        return this._dashboardCreated$;
    }

    // ------- TAILORED DASHBOARD ---------
    private isTailoredDashboardLoaded$(): Observable<boolean> {
        return this._store.pipe(select(DashboardsStoreSelectors.selectIsLoaded));
    }

    private getStableTailoredDashboards$(): Observable<TailoredDashboard[]> {
        return this.isTailoredDashboardLoaded$().pipe(
            filter(isLoaded => isLoaded),
            switchMap(() => this.getTailoredDashboards$())
        );
    }

    private getTailoredDashboards$(): Observable<TailoredDashboard[]> {
        return this._store.pipe(select(DashboardsStoreSelectors.selectDashboards));
    }

    private getTailoredDashboardById$(dashboardId: TailoredDashboardId): Observable<TailoredDashboard | null> {
        return this._store.pipe(select(DashboardsStoreSelectors.selectDashboardById(dashboardId)));
    }
}
